import ast from './ast.png'
import compositeInterface from './composite-interface.png'

const content = `<p>The Composite design pattern helps represent objects containing other objects, forming a nested structure with any level of depth. All the objects in the structure share a common interface, so that they can be used independently of their type and position in the structure, using <a href="/polymorphism">Polymorphism</a>.</p>

<p>Many hierarchical concepts can be represented in a nested structure and can be represented with a Composite pattern.</p>

<p>Let’s take an example. To evaluate a mathematical expression, we can build an AST. It is a tree where each node represents an element. Some nodes can contain other nodes.</p>

<p>For example, consider the following expression: <code>2 + 5 + 3 * 4</code>. Here is the AST that corresponds to this expression:</p>

<img src=${ast} alt="ast" />

<p>Let’s represent each type of node by a class which share a common interface: they all implement an <code>evaluate</code> method. Evaluating a value consists in returning it:</p>

<pre><code class="language-ruby">class Value
  def initialize(value)
    @value = value
  end

  def evaluate
    @value
  end
end</code></pre>

<p>A sum takes two expressions that could be of any kind as long as they implement an <code>evaluate</code> method:</p>

<pre><code class="language-ruby">class Sum
  def initialize(expression1, expression2)
    @expression1 = expression1
    @expression2 = expression2
  end

  def evaluate
    @expression1.evaluate + @expression2.evaluate
  end
end</code></pre>

<p>And similarly for multiplication:</p>

<pre><code class="language-ruby">class Multiplication
  def initialize(expression1, expression2)
    @expression1 = expression1
    @expression2 = expression2
  end

  def evaluate
    @expression1.evaluate * @expression2.evaluate
  end
end</code></pre>

<p>The tree that represents our expression <code>2 + 5 + 3 * 4</code> can be built the following way:</p>

<pre><code class="language-ruby">expression = Sum.new(
  Sum.new(
    Value.new(2),
    Value.new(5)
  ),
  Multiplication.new(
    Value.new(3),
    Value.new(4)
  )
)</code></pre>

<p>If we call <code>expression.evaluate</code> we get the correct result: <code>19</code>.</p>

<p>The <code>Sum</code> and <code>Multiplication</code> node call the <code>evaluate</code> methods of their sub-expressions, until reaching a <code>Value</code> that returns a number.</p>

<p>We can see a usage of <a href="/polymorphism">Polymorphism</a> in <code>Sum</code> (and similarly in <code>Multiplication</code>): its code is decoupled from the code of the other classes as it works with any type: values, multiplications and other sums:</p>

<img src=${compositeInterface} alt="composite-interface" />

<p>By having a interface shared by all the elements in the hierarchical structure, the Composite pattern allows to decouple all the classes from each other and from the code that holds the tree.</p>

<div class="notes">
<p>Key points</p>

<ul><li>Composite represents hierarchical structures of objects containing other objects</li><li>All the objects in the structure share the same interface</li><li>It relies on Polymorphism to achieve Low Coupling between the classes and with the code that holds the structure</li></ul>
</div>`

export default content
